import { useState, useCallback } from 'react';

const STORAGE_KEY = 'normalModeQuiz_history';
const MAX_HISTORY_ITEMS = 50;

export const useQuizHistory = () => {
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedHistory = JSON.parse(saved);
        setHistory(parsedHistory);
        return parsedHistory;
      }
    } catch (error) {
      console.warn('Failed to load quiz history from localStorage:', error);
    }
    return [];
  }, []);

  const saveQuizResult = useCallback((quizResult) => {
    try {
      const newResult = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        ...quizResult
      };

      setHistory(prev => {
        const updated = [newResult, ...prev].slice(0, MAX_HISTORY_ITEMS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });

      return newResult.id;
    } catch (error) {
      console.warn('Failed to save quiz result to localStorage:', error);
      return null;
    }
  }, []);

  const getQuizResult = useCallback((id) => {
    return history.find(result => result.id === id);
  }, [history]);

  const deleteQuizResult = useCallback((id) => {
    try {
      setHistory(prev => {
        const updated = prev.filter(result => result.id !== id);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
    } catch (error) {
      console.warn('Failed to delete quiz result from localStorage:', error);
    }
  }, []);

  const clearHistory = useCallback(() => {
    try {
      setHistory([]);
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear quiz history from localStorage:', error);
    }
  }, []);

  const getStats = useCallback(() => {
    if (history.length === 0) {
      return {
        totalQuizzes: 0,
        averageScore: 0,
        bestScore: 0,
        totalQuestionsAnswered: 0,
        averageAccuracy: 0,
        favoriteTopics: [],
        favoriteDifficulties: []
      };
    }

    const totalQuizzes = history.length;
    const totalScore = history.reduce((sum, quiz) => sum + (quiz.total_score || 0), 0);
    const averageScore = Math.round(totalScore / totalQuizzes);
    const bestScore = Math.max(...history.map(quiz => quiz.total_score || 0));
    
    const totalQuestionsAnswered = history.reduce((sum, quiz) => 
      sum + (quiz.question_results?.length || 0), 0);
    
    const correctAnswers = history.reduce((sum, quiz) => 
      sum + (quiz.question_results?.filter(q => q.isCorrect).length || 0), 0);
    
    const averageAccuracy = totalQuestionsAnswered > 0 
      ? Math.round((correctAnswers / totalQuestionsAnswered) * 100) 
      : 0;

    // Count topic and difficulty frequencies
    const topicCounts = {};
    const difficultyCounts = {};

    history.forEach(quiz => {
      if (quiz.config?.topic_name) {
        topicCounts[quiz.config.topic_name] = (topicCounts[quiz.config.topic_name] || 0) + 1;
      }
      if (quiz.config?.difficulty_name) {
        difficultyCounts[quiz.config.difficulty_name] = (difficultyCounts[quiz.config.difficulty_name] || 0) + 1;
      }
    });

    const favoriteTopics = Object.entries(topicCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([topic, count]) => ({ topic, count }));

    const favoriteDifficulties = Object.entries(difficultyCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([difficulty, count]) => ({ difficulty, count }));

    return {
      totalQuizzes,
      averageScore,
      bestScore,
      totalQuestionsAnswered,
      averageAccuracy,
      favoriteTopics,
      favoriteDifficulties
    };
  }, [history]);

  return {
    history,
    loadHistory,
    saveQuizResult,
    getQuizResult,
    deleteQuizResult,
    clearHistory,
    getStats
  };
};
